import React, { useEffect, useRef, memo } from "react";
import styled from "styled-components";
import distances from "src/assets/styles/distances.js";
import mediaQuery from "src/assets/styles/mediaQuery";
import LottieAnimation from "src/components/global/LottieAnimation.js";

import LogoBar from "src/assets/images/LogoBar.inline.svg";
import BrandPatern from "src/assets/images/BrandBar.inline.svg";
import colors from "../../assets/styles/colors";

import animation01 from "src/assets/animations/logo.json";
import { Link } from "gatsby";

const PATTERN_COUNT = 10;
const SCROLL_MULTIPLIER = 1.1;

const Wrapper = styled.aside`
  width: 20%;
  display: block;
  flex-shrink: 0;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  overflow: hidden;

  @media (max-width: ${mediaQuery.tablet}px) {
    position: fixed;
    left: 0;
    width: 100vw;
    height: 90rem;
    top: 100vh;
    transform: translateY(-100%);
    z-index: 99999;
  }
`;

const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  transform: translate3d(0, -${(props) => props.scrollPosition}px, 0);
  transition: transform 0.1s linear;
  will-change: transform;

  @media (max-width: ${mediaQuery.tablet}px) {
    transform: translate3d(-${(props) =>
      props.scrollPosition}px, 0, 0) rotate(-90deg);
    transform-origin: 45rem 45rem;
  }
`;

const StyledLogoBar = styled(Link)`
  width: 100%;
  background-color: ${colors.dark};

  @media (max-width: ${mediaQuery.tablet}px) {
    display: none;
  }
`;

const StyledBrandPatern = styled(BrandPatern)`
  @media (max-width: ${mediaQuery.tablet}px) {
    width: 90rem;
  }
`;

const BrandBar = ({ locale }) => {
  const scrollPosition = useRef(0);
  const barWrapperRef = useRef();
  const animationFrameId = useRef(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  // Memoize the transform style calculation
  const getTransformStyle = React.useCallback((scrollPos) => {
    return window.matchMedia(`(max-width: ${mediaQuery.tablet}px)`).matches
      ? `translate3d(-${scrollPos}px, 0, 0) rotate(-90deg)`
      : `translate3d(0, -${scrollPos}px, 0)`;
  }, []);

  useEffect(() => {
    function debounce(func, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }
    const handleScroll = () => {
      lastScrollY.current = window.scrollY;

      if (!ticking.current) {
        animationFrameId.current = requestAnimationFrame(() => {
          scrollPosition.current = lastScrollY.current * SCROLL_MULTIPLIER;
          if (barWrapperRef.current) {
            barWrapperRef.current.style.transform = getTransformStyle(
              scrollPosition.current
            );
          }
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    const scrollListener = window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    // Cleanup function
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [getTransformStyle]); // Add getTransformStyle to dependencies

  // Memoize the patterns array to prevent unnecessary re-renders
  const patterns = React.useMemo(
    () =>
      Array.from({ length: PATTERN_COUNT }, (_, index) => (
        <StyledBrandPatern key={`pattern-${index}`} />
      )),
    []
  );

  // Memoize the Lottie component configuration
  const lottieRef = React.useRef();
  const lottieConfig = React.useMemo(
    () => ({
      lottieRef,
      animationData: animation01,
      loop: true,
      autoplay: false,
      onMouseEnter: () => lottieRef.current?.goToAndPlay(0),
      onMouseLeave: () => lottieRef.current?.stop(),
    }),
    []
  );

  return (
    <Wrapper>
      <BarWrapper ref={barWrapperRef}>
        <StyledLogoBar to={`${locale === "pl" ? "/pl/home" : "/"}`}>
          <LottieAnimation {...lottieConfig} />
        </StyledLogoBar>
        {patterns}
      </BarWrapper>
    </Wrapper>
  );
};

export default memo(BrandBar);
